// src/components/Dashboard/DashboardViewSelector.tsx
import React, { useState, useRef, useEffect } from 'react';
import './DashboardViewSelector.css';

export type DashboardViewType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

interface ViewOption {
  id: DashboardViewType;
  name: string;
  iconClass: string;
  action: string;
  disabled?: boolean;
}

interface DashboardViewSelectorProps {
  currentViewTypeId: DashboardViewType;
  onViewChange: (viewType: DashboardViewType) => void;
  showMeView?: boolean;
  showCapacityTracker?: boolean;
}

const viewOptions: ViewOption[] = [
  {
    id: 11,
    name: 'Me View',
    iconClass: 'fa fa-user',
    action: 'showGridProd',
    disabled: false,
  },
  {
    id: 2,
    name: 'Large Cards View',
    iconClass: 'icons-corgee-card material-icons',
    action: 'showCards',
    disabled: false,
  },
  {
    id: 1,
    name: 'Small Cards View',
    iconClass: 'icons-corgee-title-card material-icons',
    action: 'showTiles',
    disabled: false,
  },
  {
    id: 3,
    name: 'Calendar View',
    iconClass: 'icons-corgee-calendar-dashboard material-icons',
    action: 'showCalendar',
    disabled: false,
  },
  {
    id: 4,
    name: 'Grid View',
    iconClass: 'fa fa-table',
    action: 'showGrid',
    disabled: false,
  },
  {
    id: 5,
    name: 'Files View',
    iconClass: 'fa fa-file-o',
    action: 'showViewFilesOrGallery(5)',
    disabled: false,
  },
  {
    id: 6,
    name: 'Gallery View',
    iconClass: 'fa fa-picture-o',
    action: 'showViewFilesOrGallery(6)',
    disabled: false,
  },
  {
    id: 7,
    name: 'Gantt View',
    iconClass: 'fa fa-tasks',
    action: 'showGantt',
    disabled: false,
  },
  {
    id: 9,
    name: 'Phase Details',
    iconClass: 'fa fa-sitemap',
    action: 'showPhaseDetails',
    disabled: false,
  },
  {
    id: 8,
    name: 'Phase Details V2',
    iconClass: 'fa fa-list-alt',
    action: 'showGridPhases',
    disabled: false,
  },
  {
    id: 10,
    name: 'Capacity Tracker',
    iconClass: 'fa fa-users',
    action: 'showCapacityTracker',
    disabled: false,
  },
];

export const DashboardViewSelector: React.FC<DashboardViewSelectorProps> = ({
  currentViewTypeId,
  onViewChange,
  showMeView = false,
  showCapacityTracker = false,
}) =>
{
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close menu
  useEffect(() =>
  {
    const handleClickOutside = (event: MouseEvent) =>
    {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      )
      {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () =>
    {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleMenu = () =>
  {
    setIsOpen(!isOpen);
  };

  const handleViewClick = (option: ViewOption) =>
  {
    if (option.disabled) return;
    onViewChange(option.id);
    setIsOpen(false);
  };

  const isDark = document.documentElement.classList.contains('dark');

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggleMenu}
        className="flex items-center justify-center w-7 h-7 hover:bg-gray-200 dark:hover:bg-[#3a3c3c] rounded"
        title="Dashboard View Selector"
      >
        <i
          className="material-icons text-lg"
          style={{ color: isDark ? '#b1a99d' : 'rgba(0, 0, 0, 0.4)' }}
        >
          apps
        </i>
      </button>

      {isOpen && (
        <div
          ref={menuRef}
          className="absolute right-0 top-full mt-1 rounded shadow-lg z-50"
          style={{
            minWidth: '160px',
            backgroundColor: isDark ? '#333535' : '#ededed',
            borderColor: isDark ? '#b1a99d' : '#ededed',
            border: '1px solid',
          }}
        >
          {viewOptions
            .filter(o => (o.id !== 11 || showMeView) && (o.id !== 10 || showCapacityTracker))
            .map(option =>
            {
              const isCurrent = currentViewTypeId === option.id;
              const isDisabled = option.disabled;
              return (
                <div
                  key={option.id}
                  onClick={() => handleViewClick(option)}
                  title={isDisabled ? 'Coming soon' : undefined}
                  className={`
                  flex items-center gap-2 px-3 h-6 text-xs text-[#b1a99d]
                  ${isDisabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}
                  ${isCurrent && !isDisabled ? (isDark ? 'bg-[#3a3c3c]' : 'bg-gray-200') : ''}
                  ${!isDisabled && !isCurrent ? (isDark ? 'hover:bg-[#3a3c3c]' : 'hover:bg-gray-50') : ''}
                `}
                >
                  <i
                    className={option.iconClass}
                    style={{
                      fontSize: '12px',
                      width: '14px',
                      color: isDark ? '#b1a99d' : 'rgba(0,0,0,0.5)',
                    }}
                  ></i>
                  <span
                    className="whitespace-nowrap"
                    style={{ color: isDark ? '#b1a99d' : 'rgba(0,0,0,0.7)' }}
                  >
                    {option.name}
                  </span>
                  {isDisabled && (
                    <span
                      className="ml-auto text-gray-400 dark:text-[#6e6860]"
                      style={{ fontSize: '10px' }}
                    >
                      Soon
                    </span>
                  )}
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default DashboardViewSelector;
